import { ViewportScroller } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { DotLottie } from '@lottiefiles/dotlottie-web';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { AppState } from './app-state';
import { GlobalStoreService } from './global/services/global-store.service';
import { loadTranslation } from './global/store/global.actions';
import { LANGUAGES } from './shared/constants/constants';
import { Expertise } from './shared/interfaces/shared-interface';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  public languages = LANGUAGES;
  public selectedLanguage = this.languages[0];
  public isAdminActive = false;
  public isLoginActive = false;
  public isAnimationProcessActive = false;
  public isFadeOut = false;
  public isInitSuccessfully = true;
  public expertisesList: string[] = [];

  private unsubscribe$ = new Subject<void>();
  private introTimers: number[] = [];

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private renderer: Renderer2,
    private globalStore: GlobalStoreService,
    private viewportScroller: ViewportScroller
  ) {}

  ngOnInit(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
    this.toggleScroll(true);
    this.startIntroAnimation();
    this.store.dispatch(
      loadTranslation({ language: this.selectedLanguage.value })
    );
    this.globalStore.fetchAllExpertises();
    this.globalStore
      .selectAllExpertises()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expertises: Expertise[]) => {
        this.expertisesList = this.flattenExpertiseTree(expertises).map(
          (expertise: Expertise) => expertise.name
        );
      });
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      const currentUrl = this.router.url;
      this.isAdminActive = currentUrl === '/admin' || currentUrl.startsWith('/admin/');

      if (currentUrl === '/login') {
        this.isLoginActive = true;
      } else {
        this.isLoginActive = false;
      }
    });

    const dotLottie = new DotLottie({
      autoplay: true,
      loop: true,
      canvas: this.canvasRef.nativeElement,
      src: '../assets/animations/azone-logo.lottie',
    });
  }

  private toggleScroll(shouldDisable: boolean): void {
    if (shouldDisable) {
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(document.body, 'overflow');
    }
  }

  private startIntroAnimation(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.isFadeOut = true;
      this.isAnimationProcessActive = true;
      this.toggleScroll(false);
      return;
    }

    this.introTimers.push(
      window.setTimeout(() => (this.isFadeOut = true), 600),
      window.setTimeout(() => {
        this.isAnimationProcessActive = true;
        this.toggleScroll(false);
      }, 3000)
    );
  }

  private flattenExpertiseTree(expertises: Expertise[]): Expertise[] {
    const flat: Expertise[] = [];

    const traverse = (nodes: Expertise[]) => {
      nodes.forEach((node) => {
        flat.push(node);
        if (node.children?.length) {
          traverse(node.children);
        }
      });
    };

    traverse(expertises || []);
    return flat;
  }

  ngOnDestroy(): void {
    this.introTimers.forEach((timerId) => window.clearTimeout(timerId));
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.toggleScroll(false);
  }
}
